/**
 * ReConsentModal Component
 * Modal displayed when user needs to sign updated consent documents
 * Uses React Portal to render at document root level for proper full-screen display
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, FileText, Check } from 'lucide-react';
import { config } from '@/src/config';

import { useAppTranslation } from '@/contexts/TranslationContext';
interface ConsentDocument {
  id: number;
  document_type: string;
  version: string;
  title: string;
  content: string;
  summary?: string;
}

interface ConsentStatus {
  terms_needs_update: boolean;
  hipaa_needs_update: boolean;
  terms_current_version?: string;
  hipaa_current_version?: string;
  terms_signed_version?: string;
  hipaa_signed_version?: string;
  all_consents_current?: boolean;
}

interface ReConsentModalProps {
  consentStatus: ConsentStatus;
  onComplete: () => void;  // Called after successful re-consent
  userEmail: string;
  accessToken: string;  // Auth token for API calls
}

type ConsentStep = 'terms' | 'hipaa' | 'complete';

export const ReConsentModal: React.FC<ReConsentModalProps> = ({
  consentStatus,
  onComplete,
  userEmail,
  accessToken
}) => {
  const { t } = useAppTranslation();
  const [currentStep, setCurrentStep] = useState<ConsentStep>('terms');
  const [documents, setDocuments] = useState<{terms?: ConsentDocument, hipaa?: ConsentDocument}>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Terms of Use state
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsSignature, setTermsSignature] = useState('');
  const [termsSignatureConfirm, setTermsSignatureConfirm] = useState(false);

  // HIPAA Authorization state
  const [hipaaAccepted, setHipaaAccepted] = useState(false);
  const [hipaaSignature, setHipaaSignature] = useState('');
  const [hipaaSignatureConfirm, setHipaaSignatureConfirm] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchConsentDocuments();

    // Determine starting step
    if (consentStatus.terms_needs_update) {
      setCurrentStep('terms');
    } else if (consentStatus.hipaa_needs_update) {
      setCurrentStep('hipaa');
    }
  }, [consentStatus]);

  const fetchConsentDocuments = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${config.weaveinApi.baseUrl}/api/consent/documents/active`);

      if (!response.ok) {
        throw new Error('Failed to load consent documents');
      }

      const docs: ConsentDocument[] = await response.json();

      const termsDoc = docs.find(d => d.document_type === 'terms_of_use');
      const hipaaDoc = docs.find(d => d.document_type === 'hipaa_authorization');

      setDocuments({
        terms: termsDoc,
        hipaa: hipaaDoc
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load consent documents');
    } finally {
      setLoading(false);
    }
  };

  const handleTermsContinue = () => {
    if (!termsAccepted || !termsSignature.trim() || !termsSignatureConfirm) {
      alert('Please accept the terms and provide your signature');
      return;
    }

    if (consentStatus.hipaa_needs_update) {
      setCurrentStep('hipaa');
    } else {
      // Only terms needs update, complete immediately
      handleSubmit();
    }
  };

  const handleHipaaComplete = () => {
    if (!hipaaAccepted || !hipaaSignature.trim() || !hipaaSignatureConfirm) {
      alert('Please accept the HIPAA authorization and provide your signature');
      return;
    }

    handleSubmit();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      // Sign Terms of Use if needed
      if (consentStatus.terms_needs_update && documents.terms) {
        const termsResponse = await fetch(`${config.weaveinApi.baseUrl}/api/consent/sign`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            document_id: documents.terms.id,
            signature_text: termsSignature
          })
        });

        if (!termsResponse.ok) {
          const errorData = await termsResponse.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to sign Terms of Use');
        }
      }

      // Sign HIPAA Authorization if needed
      if (consentStatus.hipaa_needs_update && documents.hipaa) {
        const hipaaResponse = await fetch(`${config.weaveinApi.baseUrl}/api/consent/sign`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            document_id: documents.hipaa.id,
            signature_text: hipaaSignature
          })
        });

        if (!hipaaResponse.ok) {
          const errorData = await hipaaResponse.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to sign HIPAA Authorization');
        }
      }

      // Success - all consents signed
      setCurrentStep('complete');

      // Auto-close after 2 seconds
      setTimeout(() => {
        onComplete();
      }, 2000);

    } catch (err: any) {
      setError(err.message || 'Failed to submit consent signatures');
    } finally {
      setSubmitting(false);
    }
  };

  // Render modal content
  const renderModalContent = () => {
    if (loading) {
      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-8 max-w-md w-full">
            <div className="text-center text-gray-600 dark:text-gray-300">{t('auth.loading_consent_documents', 'Loading consent documents...')}</div>
          </div>
        </div>
      );
    }

    if ((error && !documents.terms && !documents.hipaa) || (!loading && !documents.terms && !documents.hipaa)) {
      // No active consent documents found — skip the modal entirely
      // This prevents a deadlock where the modal shows but has nothing to sign
      if (!error) {
        onComplete();
        return null;
      }
      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-8 max-w-md w-full">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 dark:text-red-400">{error}</p>
            </div>
          </div>
        </div>
      );
    }

    if (currentStep === 'complete') {
      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-8 max-w-md w-full text-center">
            <Check className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{t('auth.consent_updated_successfully', 'Consent Updated Successfully')}</h2>
            <p className="text-gray-600 dark:text-gray-300">{t('auth.thank_you_for_reviewing_and', 'Thank you for reviewing and signing the updated consent documents.')}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[70] p-0 overflow-y-auto">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl my-4 mx-auto" style={{
          width: '95vw',
          maxWidth: '1000px',
          maxHeight: '95vh',
          display: 'flex',
          flexDirection: 'column'
        }}>
        {/* Header */}
        <div className="bg-yellow-50 dark:bg-yellow-900/30 border-b border-yellow-200 dark:border-yellow-700 p-6">
          <div className="flex items-start gap-4">
            <AlertCircle className="w-8 h-8 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{t('auth.consent_update_required', 'Consent Update Required')}</h2>
              <p className="text-gray-700 dark:text-gray-300">
                We've updated our {consentStatus.terms_needs_update && consentStatus.hipaa_needs_update ? 'Terms of Use and HIPAA Authorization' : consentStatus.terms_needs_update ? 'Terms of Use' : 'HIPAA Authorization'}.
                Please review and sign the updated documents to continue.
              </p>
            </div>
          </div>
        </div>

        {/* Progress */}
        {consentStatus.terms_needs_update && consentStatus.hipaa_needs_update && (
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <span className={`text-sm font-medium ${currentStep === 'terms' ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400'}`}>
                {t('auth.step_1_terms_of_use', 'Step 1: Terms of Use')}
              </span>
              <span className={`text-sm font-medium ${currentStep === 'hipaa' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`}>
                {t('auth.step_2_hipaa_authorization', 'Step 2: HIPAA Authorization')}
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="bg-blue-600 dark:bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: currentStep === 'terms' ? '50%' : '100%' }}
              />
            </div>
          </div>
        )}

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          {currentStep === 'terms' && documents.terms && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">{documents.terms.title}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Version {documents.terms.version}</p>
                </div>
              </div>

              {documents.terms.summary && (
                <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-900 dark:text-blue-200"><strong>{t('auth.whats_new', 'What\'s New:')}</strong> {documents.terms.summary}</p>
                </div>
              )}

              <div className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg p-4 mb-6 max-h-96 overflow-y-auto">
                <pre className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300 font-sans">
                  {documents.terms.content}
                </pre>
              </div>

              <div className="mb-6">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-1 h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {t('auth.i_have_read_understood_and', 'I have read, understood, and agree to the updated Terms of Use')}
                  </span>
                </label>
              </div>

              <div className="mb-6 p-4 border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800">
                <h4 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">{t('auth.electronic_signature', 'Electronic Signature')}</h4>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('auth.type_your_full_name', 'Type Your Full Name')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={termsSignature}
                    onChange={(e) => setTermsSignature(e.target.value)}
                    placeholder={t('auth.enter_your_full_legal_name', 'Enter your full legal name')}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 text-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    style={{ fontFamily: 'Brush Script MT, cursive' }}
                  />
                </div>
                <div className="mb-4">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={termsSignatureConfirm}
                      onChange={(e) => setTermsSignatureConfirm(e.target.checked)}
                      className="mt-1 h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {t('auth.i_confirm_this_electronic_signature', 'I confirm this electronic signature is legally binding')}
                    </span>
                  </label>
                </div>
              </div>

              {error && (
                <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg text-red-700 dark:text-red-400">
                  {error}
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={handleTermsContinue}
                  disabled={!termsAccepted || !termsSignature.trim() || !termsSignatureConfirm || submitting}
                  className="px-6 py-3 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed font-medium"
                >
                  {consentStatus.hipaa_needs_update ? 'Continue to HIPAA Authorization' : 'Complete'}
                </button>
              </div>
            </div>
          )}

          {currentStep === 'hipaa' && documents.hipaa && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">{documents.hipaa.title}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Version {documents.hipaa.version}</p>
                </div>
              </div>

              {documents.hipaa.summary && (
                <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-900 dark:text-blue-200"><strong>{t('auth.whats_new_1', 'What\'s New:')}</strong> {documents.hipaa.summary}</p>
                </div>
              )}

              <div className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg p-4 mb-6 max-h-96 overflow-y-auto">
                <pre className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300 font-sans">
                  {documents.hipaa.content}
                </pre>
              </div>

              <div className="mb-6">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hipaaAccepted}
                    onChange={(e) => setHipaaAccepted(e.target.checked)}
                    className="mt-1 h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {t('auth.i_hereby_authorize_the_use', 'I hereby authorize the use and disclosure of my protected health information as described')}
                  </span>
                </label>
              </div>

              <div className="mb-6 p-4 border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800">
                <h4 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">{t('auth.electronic_signature_1', 'Electronic Signature')}</h4>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('auth.type_your_full_name_1', 'Type Your Full Name')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={hipaaSignature}
                    onChange={(e) => setHipaaSignature(e.target.value)}
                    placeholder={t('auth.enter_your_full_legal_name_1', 'Enter your full legal name')}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 text-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    style={{ fontFamily: 'Brush Script MT, cursive' }}
                  />
                </div>
                <div className="mb-4">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hipaaSignatureConfirm}
                      onChange={(e) => setHipaaSignatureConfirm(e.target.checked)}
                      className="mt-1 h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {t('auth.i_confirm_this_electronic_signature_1', 'I confirm this electronic signature is legally binding')}
                    </span>
                  </label>
                </div>
              </div>

              {error && (
                <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg text-red-700 dark:text-red-400">
                  {error}
                </div>
              )}

              <div className="flex justify-between">
                {consentStatus.terms_needs_update && (
                  <button
                    onClick={() => setCurrentStep('terms')}
                    className="px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                    disabled={submitting}
                  >
                    {t('auth.back_to_terms', 'Back to Terms')}
                  </button>
                )}
                <button
                  onClick={handleHipaaComplete}
                  disabled={!hipaaAccepted || !hipaaSignature.trim() || !hipaaSignatureConfirm || submitting}
                  className="ml-auto px-6 py-3 bg-green-600 dark:bg-green-500 text-white rounded-md hover:bg-green-700 dark:hover:bg-green-600 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed font-medium"
                >
                  {submitting ? 'Submitting...' : 'Complete'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    );
  };

  // Use React Portal to render at document root level, bypassing parent constraints
  return createPortal(
    renderModalContent(),
    document.body
  );
};
